@if (isOpen && ticket) {
  <div class="panel-overlay" (click)="onOverlayClick($event)" (keydown.escape)="onClose()">
    <div class="detail-panel" data-testid="ticket-detail-panel">
      <!-- Header -->
      <div class="panel-header">
        <h2>Support Ticket</h2>
        <button mat-icon-button (click)="onClose()" data-testid="close-panel-button">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      @if (uiState === uiStateEnum.ShowLoading) {
        <div class="loading-container">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      } @else {
        <div class="panel-content">
          <!-- Status Banner -->
          @if (ticket.Escalated && !ticket.Resolved) {
            <div class="status-banner escalated">
              <mat-icon>priority_high</mat-icon>
              <span>This ticket has been escalated to the Causable team</span>
            </div>
          }
          @if (ticket.Resolved) {
            <div class="status-banner resolved">
              <mat-icon>check_circle</mat-icon>
              <span>This ticket has been resolved</span>
            </div>
          }

          <!-- Player Info Section -->
          <section class="info-section">
            <h3>Player Information</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Name</span>
                <span class="info-value" data-testid="panel-player-name">{{ ticket.PlayerName }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Email</span>
                <span class="info-value" data-testid="panel-player-email">{{ ticket.PlayerEmail }}</span>
              </div>
            </div>
            <button mat-stroked-button
                    class="view-player-tickets-btn"
                    (click)="onViewPlayerTickets()"
                    data-testid="view-player-tickets">
              <mat-icon>filter_list</mat-icon>
              View all tickets for this player
            </button>
          </section>

          <mat-divider></mat-divider>

          <!-- Ticket Details Section -->
          <section class="info-section">
            <h3>Ticket Details</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Issue Type</span>
                <span class="info-value">{{ errorTypeLabel() }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Created</span>
                <span class="info-value">{{ ticket.CreatedOn | date:'medium' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Assignee</span>
                <span class="info-value">{{ assigneeName() }}</span>
              </div>
              @if (ticket.SentryEventId) {
                <div class="info-item full-width">
                  <span class="info-label">Sentry Event ID</span>
                  <span class="info-value monospace">{{ ticket.SentryEventId }}</span>
                </div>
              }
            </div>
          </section>

          <mat-divider></mat-divider>

          <!-- Description Section -->
          <section class="info-section">
            <h3>Issue Description</h3>
            <div class="description-box" data-testid="panel-description">
              {{ ticket.ErrorDescription || 'No description provided' }}
            </div>
          </section>

          @if (ticket.Resolved && ticket.ResolutionDescription) {
            <mat-divider></mat-divider>
            <section class="info-section">
              <h3>Resolution</h3>
              <div class="description-box resolved-description">
                {{ ticket.ResolutionDescription }}
              </div>
              @if (ticket.ResolvedOn) {
                <div class="resolved-meta">
                  Resolved on {{ ticket.ResolvedOn | date:'medium' }}
                </div>
              }
            </section>
          }

          <mat-divider></mat-divider>

          <!-- Messages Section (Player Communication) -->
          <section class="info-section messages-section">
            <h3>
              <mat-icon>chat</mat-icon>
              Player Communication
            </h3>
            <p class="section-description">Messages sent here will be emailed to the player. They can respond via a link in the email.</p>

            <!-- Messages List -->
            @if (messages.length > 0) {
              <div class="messages-list" data-testid="messages-list">
                @for (message of messages; track message.Id) {
                  <div class="message-item"
                       [class.from-player]="message.IsFromPlayer"
                       [class.from-admin]="!message.IsFromPlayer"
                       [attr.data-testid]="message.IsFromPlayer ? 'message-from-player' : 'message-from-admin'">
                    <div class="message-header">
                      <span class="message-author">
                        @if (message.IsFromPlayer) {
                          <mat-icon>person</mat-icon>
                        } @else {
                          <mat-icon>support_agent</mat-icon>
                        }
                        {{ getMessageAuthorName(message) }}
                      </span>
                      <span class="message-date">{{ message.CreatedOn | date:'medium' }}</span>
                    </div>
                    <div class="message-content" data-testid="message-content">{{ message.Message }}</div>
                  </div>
                }
              </div>
            } @else {
              <div class="no-messages" data-testid="no-messages">No messages yet. Send a message to start communicating with the player.</div>
            }

            <!-- Send Message Form -->
            @if (!ticket.Resolved) {
              <div class="send-message-form" *hasPermission="permissionTypes.ModifySupportRequests">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Send message to player</mat-label>
                  <textarea matInput
                            [(ngModel)]="newMessageText"
                            rows="3"
                            placeholder="Type your message to the player..."
                            [disabled]="isSubmittingMessage"
                            data-testid="panel-message-input"></textarea>
                </mat-form-field>
                <button mat-flat-button
                        color="primary"
                        (click)="sendMessage()"
                        [disabled]="!newMessageText.trim() || isSubmittingMessage"
                        data-testid="panel-send-message">
                  @if (isSubmittingMessage) {
                    <mat-spinner diameter="20"></mat-spinner>
                  } @else {
                    <mat-icon>send</mat-icon>
                    Send Message
                  }
                </button>
              </div>
            }
          </section>

          <mat-divider></mat-divider>

          <!-- Notes Section (Internal) -->
          <section class="info-section notes-section">
            <h3>
              <mat-icon>note</mat-icon>
              Internal Notes
            </h3>
            <p class="section-description">Internal notes are only visible to support staff.</p>

            <!-- Add Note Form -->
            <div class="add-note-form" *hasPermission="permissionTypes.ModifySupportRequests">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Add a note</mat-label>
                <textarea matInput
                          [(ngModel)]="newNoteText"
                          rows="2"
                          placeholder="Add a note about this ticket..."
                          [disabled]="isSubmittingNote"
                          data-testid="panel-note-input"></textarea>
              </mat-form-field>
              <button mat-flat-button
                      color="primary"
                      (click)="addNote()"
                      [disabled]="!newNoteText.trim() || isSubmittingNote"
                      data-testid="panel-add-note">
                @if (isSubmittingNote) {
                  <mat-spinner diameter="20"></mat-spinner>
                } @else {
                  <mat-icon>add</mat-icon>
                  Add Note
                }
              </button>
            </div>

            <!-- Notes List -->
            @if (notes.length > 0) {
              <div class="notes-list">
                @for (note of notes; track note.Id) {
                  <div class="note-item">
                    <div class="note-header">
                      <span class="note-author">{{ getNoteAuthorName(note.CreatedBy) }}</span>
                      <span class="note-date">{{ note.CreatedOn | date:'medium' }}</span>
                    </div>
                    <div class="note-content">{{ note.Note }}</div>
                  </div>
                }
              </div>
            } @else {
              <div class="no-notes">No notes yet</div>
            }
          </section>

          <mat-divider></mat-divider>

          <!-- Actions Section -->
          <section class="actions-section" *hasPermission="permissionTypes.ModifySupportRequests">
            <h3>Actions</h3>

            @if (!ticket.Resolved && !ticket.Escalated) {
              <!-- Assign Section -->
              <div class="action-group">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Assign to</mat-label>
                  <mat-select [(value)]="selectedAssigneeId"
                              (selectionChange)="assignRequest()"
                              [disabled]="uiState === uiStateEnum.ShowRequestProcessing"
                              data-testid="panel-assignee-select">
                    @for (admin of adminsToAssign(); track admin.Id) {
                      <mat-option [value]="admin.Id">
                        {{ admin.FirstName }} {{ admin.LastName }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                @if (!isAssignedToMe()) {
                  <button mat-stroked-button
                          (click)="assignToMe()"
                          [disabled]="uiState === uiStateEnum.ShowRequestProcessing"
                          data-testid="panel-assign-to-me">
                    <mat-icon>person_add</mat-icon>
                    Assign to me
                  </button>
                }
              </div>
            }

            <!-- Resolve Section (game admins cannot resolve escalated tickets) -->
            @if (!ticket.Resolved && (isCrossGameView || !ticket.Escalated)) {
              @if (!showResolveForm) {
                <button mat-flat-button
                        color="primary"
                        class="full-width action-button"
                        (click)="toggleResolveForm()"
                        [disabled]="uiState === uiStateEnum.ShowRequestProcessing"
                        data-testid="panel-show-resolve-form">
                  <mat-icon>check_circle</mat-icon>
                  Resolve Ticket
                </button>
              } @else {
                <div class="resolve-form">
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Resolution Description</mat-label>
                    <textarea matInput
                              [formControl]="resolutionDescriptionControl"
                              rows="3"
                              placeholder="Describe how you resolved this issue..."
                              data-testid="panel-resolution-input"></textarea>
                    @if (resolutionDescriptionControl.touched && resolutionDescriptionControl.errors) {
                      <mat-error>Resolution description is required</mat-error>
                    }
                  </mat-form-field>
                  <div class="resolve-actions">
                    <button mat-stroked-button
                            (click)="toggleResolveForm()"
                            [disabled]="uiState === uiStateEnum.ShowRequestProcessing">
                      Cancel
                    </button>
                    <button mat-flat-button
                            color="primary"
                            (click)="resolveTicket()"
                            [disabled]="uiState === uiStateEnum.ShowRequestProcessing"
                            data-testid="panel-confirm-resolve">
                      @if (uiState === uiStateEnum.ShowRequestProcessing) {
                        <mat-spinner diameter="20"></mat-spinner>
                      } @else {
                        Confirm Resolution
                      }
                    </button>
                  </div>
                </div>
              }
            }

            <!-- Escalate Button (only for game admins, not cross-game, and not already escalated) -->
            @if (!ticket.Resolved && !isCrossGameView && !ticket.Escalated) {
              <button mat-stroked-button
                      color="warn"
                      class="full-width action-button"
                      (click)="escalateTicket()"
                      [disabled]="uiState === uiStateEnum.ShowRequestProcessing"
                      data-testid="panel-escalate">
                <mat-icon>arrow_upward</mat-icon>
                Escalate to Causable Team
              </button>
            }

            <!-- Reopen Button (only for resolved tickets, and game admins cannot reopen escalated tickets) -->
            @if (ticket.Resolved && (isCrossGameView || !ticket.Escalated)) {
              <button mat-stroked-button
                      class="full-width action-button"
                      (click)="reopenTicket()"
                      [disabled]="uiState === uiStateEnum.ShowRequestProcessing"
                      data-testid="panel-reopen">
                @if (uiState === uiStateEnum.ShowRequestProcessing) {
                  <mat-spinner diameter="20"></mat-spinner>
                } @else {
                  <mat-icon>refresh</mat-icon>
                  Reopen Ticket
                }
              </button>
            }
          </section>
        </div>
      }
    </div>
  </div>
}
